/**
 * Parse a disciplined-compose slide's builder payload into a readable, structured shape for the
 * storyboard. The richest field is `visualInstructions` — the act-by-act shot list with the
 * archetype/surface tags. This parser also doubles as the FALLBACK that lights up videos planned
 * before the structured `storyboard` field existed (archetype/tier/surface live only in the text).
 */
import type { SlideOutline, StoryboardMeta } from '@/types/outline';

export interface ParsedAct {
  /** "ACT 1" / "PAGE 1" / "OVERVIEW" */
  label: string;
  /** e.g. "~0-18s" */
  window?: string;
  /** "archetype" | "generative" | undefined */
  actKind?: string;
  /** the act body prose (cleaned) */
  body: string;
  /** verbatim on-screen text quoted in this act */
  onScreenText: string[];
}

export interface ParsedStoryboard {
  archetypeKey?: string;
  tier?: string;
  surface?: string;
  chapter?: string;
  sceneIndex?: string;
  lessonFrame?: string;
  /** intro prose before the first act block */
  intro?: string;
  acts: ParsedAct[];
  /** all on-screen text across acts, deduped, in order */
  onScreenText: string[];
}

const tag = (text: string, name: string): string | undefined => {
  const m = text.match(new RegExp(`\\[${name}:\\s*([^\\]]+)\\]`, 'i'));
  return m ? m[1].trim() : undefined;
};

const onScreenTextOf = (block: string): string[] =>
  [...block.matchAll(/onScreenText:\s*"([^"]+)"/gi)].map((m) => m[1].trim());

// Uppercase only — real act headers are "ACT 1"/"PAGE 1"; an inline lowercase "act 2" in prose
// (e.g. "exit before act 2") is NOT a header and must not split the block.
const ACT_HEAD_RE = /^(ACT\s+\d+|PAGE\s+\d+)\b/;
const WINDOW_RE = /\(~?([^)]*\d[^)]*)\)|~\s*([\d.]+\s*[-–]\s*[\d.]+\s*s)/;

/**
 * Parse the slide's storyboard payload. Prefers the structured `storyboard` field for metadata,
 * falls back to the `[archetype:]` tags inside visualInstructions, and parses the act/page blocks.
 * Robust to the two real formats: tags + acts on their own lines, AND everything inline on one line
 * (lesson frames: "[tags] course-title: … | … ACT 1 (~0-6s) …").
 */
export function parseStoryboardPayload(slide: SlideOutline, storyboard?: StoryboardMeta): ParsedStoryboard {
  const vi = slide.visualInstructions ?? '';

  // Metadata — structured field first, else parse the tags (which can appear anywhere).
  const archetypeKey = storyboard?.archetypeKey || tag(vi, 'archetype');
  const tier = storyboard?.tier || tag(vi, 'tier');
  const surface = storyboard?.surface || tag(vi, 'surface');
  const chapter = storyboard?.chapterLabel || tag(vi, 'chapter');
  const sceneIndex = storyboard?.sceneIndex || tag(vi, 'scene');
  const lessonFrame = tag(vi, 'lessonFrame');

  // Strip the leading bracket-tag cluster, then split on ACT/PAGE markers anywhere (inline or line-start).
  const body = vi.replace(/^\s*(?:\[[^\]]*\]\s*)+/, '').trim();
  const parts = body.split(/(?=\b(?:ACT|PAGE)\s+\d+\b)/);

  let intro: string | undefined;
  const acts: ParsedAct[] = [];
  for (const part of parts) {
    const block = part.trim();
    if (!block) continue;
    if (ACT_HEAD_RE.test(block)) {
      const label = block.match(ACT_HEAD_RE)![1].replace(/\s+/g, ' ').trim();
      const winMatch = block.match(WINDOW_RE);
      const window = winMatch ? (winMatch[1] || winMatch[2])?.trim().replace(/^~/, '') : undefined;
      acts.push({ label, window: window ? `~${window}` : undefined, actKind: tag(block, 'actKind'), body: block, onScreenText: onScreenTextOf(block) });
    } else {
      intro = intro ? `${intro}\n${block}` : block;
    }
  }
  // No act structure at all — show the body as one overview block.
  if (acts.length === 0 && intro) {
    acts.push({ label: 'Overview', body: intro, onScreenText: onScreenTextOf(intro) });
    intro = undefined;
  }

  // All on-screen text, deduped, in order.
  const seen = new Set<string>();
  const onScreenText: string[] = [];
  for (const a of acts) for (const t of a.onScreenText) if (!seen.has(t)) { seen.add(t); onScreenText.push(t); }

  return { archetypeKey, tier, surface, chapter, sceneIndex, lessonFrame, intro, acts, onScreenText };
}

/** Build a StoryboardMeta from the parsed payload (fallback when slide.storyboard is absent). */
export function storyboardMetaFromParsed(p: ParsedStoryboard): StoryboardMeta | undefined {
  if (!p.archetypeKey && !p.lessonFrame) return undefined;
  return {
    archetypeKey: p.archetypeKey || p.lessonFrame,
    tier: (p.tier as StoryboardMeta['tier']) || (p.lessonFrame ? 'A' : undefined),
    surface: (p.surface as StoryboardMeta['surface']) || undefined,
    chapterLabel: p.chapter,
    sceneIndex: p.sceneIndex,
  };
}
